import React, { useEffect, useState } from 'react';
import { ViewType, ProductData } from '../types';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

interface WelcomeViewProps {
  productData: ProductData;
  onViewChange: (view: ViewType) => void;
  category?: string;
  apiUrl?: string;
}

const VITE_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const VITE_SUPABASE_ASSETS_BASE = import.meta.env.VITE_SUPABASE_ASSETS_BASE as string | undefined;
const __WIN_ASSETS_BASE = (typeof window !== 'undefined' && (window as any).__ASSETS_BASE) as string | undefined;
const ASSETS_BASE = (((__WIN_ASSETS_BASE && __WIN_ASSETS_BASE.trim())
  || (VITE_SUPABASE_ASSETS_BASE && VITE_SUPABASE_ASSETS_BASE.trim())
  || (VITE_SUPABASE_URL ? `${VITE_SUPABASE_URL.replace(/\/+$/, '')}/storage/v1/object/public` : '')
) || '').replace(/\/+$/,'');
const CATEGORY_IMAGES: Record<string, string> = {
  FASHION: `${ASSETS_BASE}/Content_general/Category_image_FASHION.png`,
  BIKES: `${ASSETS_BASE}/Content_general/Category_image_BIKES.png`,
  SHOES: `${ASSETS_BASE}/Content_general/Category_image_SHOES.png`,
  MOTORS: `${ASSETS_BASE}/Content_general/Category_image_MOTORS.png`,
  GLASSES: `${ASSETS_BASE}/Content_general/Category_image_GLASSES.png`,
  JEWELRY: `${ASSETS_BASE}/Content_general/Category_image_JEWELRY.png`,
  WATCHES: `${ASSETS_BASE}/Content_general/Category_image_WATCHES.png`,
  AUTOMOTIVE: `${ASSETS_BASE}/Content_general/Category_image_AUTOMOTIVE.png`,
  FURNITURE: `${ASSETS_BASE}/Content_general/Category_image_FURNITURE.png`,
  BAGS: `${ASSETS_BASE}/Content_general/Category_image_BAGS.png`
};

export const WelcomeView: React.FC<WelcomeViewProps> = ({ productData, onViewChange, category, apiUrl }) => {
  const { t, i18n } = useTranslation();
  const key = String(category || '').toUpperCase();
  const [heroSrc, setHeroSrc] = useState<string>(CATEGORY_IMAGES[key] || CATEGORY_IMAGES['FASHION']);
  const [promo, setPromo] = useState<{ header: string; body: string; video_url: string }>({ header: '', body: '', video_url: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!apiUrl || !key) return;
        const q = new URLSearchParams();
        q.set('category', key);
        const lang = String((i18n?.language || 'nl')).toLowerCase().startsWith('en') ? 'en' : 'nl';
        q.set('lang', lang);
        const r = await fetch(`${apiUrl.replace(/\/+$/,'')}/api/widget/category-config?${q.toString()}`);
        const j = await r.json().catch(() => ({}));
        const url = j?.hero;
        if (!cancelled && url && typeof url === 'string') {
          setHeroSrc(String(url));
        }
        try {
          const p = j?.promo || {};
          const cur = p?.[lang] || {};
          const altLang = lang === 'en' ? 'nl' : 'en';
          const alt = p?.[altLang] || {};
          const header = String(cur.header || alt.header || '');
          const body = String(cur.body || alt.body || '');
          const video_url = String(cur.video_url || '');
          if (!cancelled) setPromo({ header, body, video_url });
        } catch {}
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [apiUrl, key, i18n?.language]);

  return (
    <div className="modal-content--full">
      {/* Category hero image full width + language switcher */}
      <div className="w-full relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>
        <img src={heroSrc} alt={t('modal.categoryAlt')} className="w-full h-auto object-cover" />
      </div>
      {/* Centered welcome content pushed down */}
      <div className="px-6 py-10 max-w-2xl mx-auto text-center mt-6">
        <h2 className="welcome-text">{t('modal.welcome')}</h2>
        <p className="welcome-subtitle">{t('modal.welcomeSubtitle')}</p>
        <div className="flex items-center justify-center gap-3 mt-6 mb-3">
          <button 
            className="btn btn-primary"
            onClick={() => { try { sessionStorage.removeItem('fit_guest_selected'); localStorage.removeItem('fit_guest_selected_persist'); } catch {} ; onViewChange('login'); }}
          >
            {t('auth.login')}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => { try { sessionStorage.removeItem('fit_guest_selected'); localStorage.removeItem('fit_guest_selected_persist'); } catch {} ; onViewChange('register'); }}
          >
            {t('auth.createAccount')}
          </button>
        </div>
        <div className="divider">
          <span>{t('common.or')}</span>
        </div>
        <button 
          className="btn btn-link"
          onClick={() => { try { sessionStorage.setItem('fit_guest_selected','true'); localStorage.setItem('fit_guest_selected_persist','true'); } catch {} ; onViewChange('fit-session'); }}
        >
          {t('modal.continueAsGuest')}
        </button>
      </div>
      {(false) && (
        <div className="px-6 mt-3">
          <div className="w-full bg-white border border-gray-200 rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm">
            <div className="min-w-0">
              {promo.header ? (<h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{promo.header}</h3>) : null}
              {promo.body ? (<p className="text-sm text-gray-700">{promo.body}</p>) : null}
            </div>
            {promo.video_url ? (
              <div className="shrink-0">
                <a href={promo.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-orange-700 hover:text-orange-800">
                  <span>{(i18n?.language || '').toLowerCase().startsWith('en') ? 'Watch demo' : 'Bekijk demo'}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z"/><path d="M5 5h5V3H3v7h2V5z"/><path fill="none" d="M0 0h24v24H0z"/></svg>
                </a>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
;
